/**
 * Passo 2 do wizard: a prontidão da conta (spec 2026-10-09 §6, card 24897).
 *
 * Seis itens lidos pelo @DevOps na conta do tenant, cada um ok / falta / não
 * conferido, com uma frase. "Não conferido" nunca aparece como ok, e bloqueia
 * o Confirmar como "falta" (a causa vai escrita na revisão).
 *
 * - Zona do ambiente, segredos e vínculo: "Preparar agora" roda o passo do
 *   motor de provisionamento e reconfere o item; só no computador.
 * - O resto (zona do projeto, roles do runner, repositório): pendência com
 *   "Abrir chamado" — o chamado de habilitação da conta, aberto ou reaproveitado.
 *
 * A leitura vive em `useProntidao`, chamada pelo WIZARD (não por este passo):
 * quem pula direto para a Revisão também tem o Confirmar bloqueado até a conta
 * ser conferida.
 */
import { useState } from 'react'
import { Anchor, Button, Group, Stack, Text } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconAlertTriangle, IconCircleCheck, IconCircleX, IconRefresh } from '@tabler/icons-react'
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import axios from 'axios'
import {
  abrirChamadoDoItem, lerProntidao, prepararItem,
  type EstadoDoItem, type ItemDaProntidao, type ProntidaoDaConta,
} from '@/api/rascunhoDeAmbiente'
import { CHAVE_PEDIDO_TERRAFORM } from '@/hooks/useTerraformChangeRequest'
import { Dado } from '../components/Dado'
import { LeituraDoDevops } from './LeituraDoDevops'
import type { Bloqueio, PassoProps } from './Passos'

const BARLOW = { fontFamily: '"Barlow", Arial, sans-serif' }

export const chaveDaProntidao = (tenantId: number, rascunhoId: number) =>
  [CHAVE_PEDIDO_TERRAFORM, 'formulario-prontidao', tenantId, rascunhoId] as const

export function useProntidao(tenantId: number, rascunhoId: number, aberto: boolean) {
  return useQuery({
    queryKey: chaveDaProntidao(tenantId, rascunhoId),
    queryFn: () => lerProntidao(tenantId, rascunhoId),
    enabled: aberto,
    retry: false,
    staleTime: 60_000,
  })
}

/** O que a prontidão impede, em frase: o item que falta, o não conferido, ou
 * a leitura que ainda não voltou. */
export function bloqueiosDaProntidao(q: UseQueryResult<ProntidaoDaConta>): Bloqueio[] {
  if (q.isLoading) return [{ chave: 'conta:lendo', texto: 'A conta ainda está sendo conferida' }]
  if (q.error || !q.data) {
    return [{ chave: 'conta:erro', texto: 'Não consegui conferir a prontidão da conta' }]
  }
  return q.data.itens
    .filter((i) => i.estado !== 'ok')
    .map((i) => ({
      chave: `conta:${i.nome}`,
      texto: i.estado === 'falta' ? `${i.rotulo}: ${i.frase}` : `${i.rotulo}: não conferido`,
    }))
}

function motivoDoErro(e: unknown): string {
  if (axios.isAxiosError(e)) {
    const d = e.response?.data?.detail
    if (typeof d === 'object' && d?.motivo) return String(d.motivo)
    if (typeof d === 'string') return d
  }
  return 'o @DevOps não respondeu'
}

const ICONE: Record<EstadoDoItem, React.ReactNode> = {
  ok: <IconCircleCheck size={16} stroke={1.5} color="var(--mantine-color-teal-7)" aria-hidden />,
  falta: <IconCircleX size={16} stroke={1.5} color="var(--mantine-color-red-7)" aria-hidden />,
  nao_conferido: <IconAlertTriangle size={16} stroke={1.5} color="var(--mantine-color-yellow-8)" aria-hidden />,
}

const ESTADO: Record<EstadoDoItem, string> = {
  ok: 'ok', falta: 'falta', nao_conferido: 'não conferido',
}

interface ItemProps {
  item: ItemDaProntidao
  tenantId: number
  rascunhoId: number
  celular: boolean
  onConferir: () => void
}

function ItemDaConta({ item, tenantId, rascunhoId, celular, onConferir }: ItemProps) {
  const qc = useQueryClient()
  const [aviso, setAviso] = useState<string | null>(null)
  const trocarItem = (novo: ItemDaProntidao) => {
    qc.setQueryData<ProntidaoDaConta>(chaveDaProntidao(tenantId, rascunhoId), (antes) => antes && ({
      ...antes,
      itens: antes.itens.map((i) => (i.nome === novo.nome ? novo : i)),
      bloqueia: antes.itens.map((i) => (i.nome === novo.nome ? novo : i))
        .filter((i) => i.estado !== 'ok').map((i) => i.nome),
    }))
  }
  const preparar = useMutation({
    mutationFn: () => prepararItem(tenantId, rascunhoId, item.nome),
    onSuccess: (r) => {
      trocarItem(r.item)
      setAviso(r.feito ? null : `Não ficou pronto: ${r.motivo}${r.faltando.length ? ` (${r.faltando.join(', ')})` : ''}.`)
    },
    onError: (e) => setAviso(`Não preparei: ${motivoDoErro(e)}.`),
  })
  const chamado = useMutation({
    mutationFn: () => abrirChamadoDoItem(tenantId, rascunhoId, item.nome),
    onSuccess: (r) => { trocarItem(r.item); setAviso(null) },
    onError: (e) => setAviso(`O chamado não abriu: ${motivoDoErro(e)}.`),
  })

  return (
    <Stack gap={4} data-item={item.nome} data-estado={item.estado}>
      <Group gap={8} wrap="nowrap" align="flex-start">
        {ICONE[item.estado]}
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Text size="sm" fw={600} style={BARLOW}>
            {item.rotulo} <Text span size="xs" c="dimmed" fw={400}>· {ESTADO[item.estado]}</Text>
          </Text>
          <Text size="xs" style={BARLOW}>{item.frase}</Text>
        </Stack>
      </Group>
      <Stack gap={4} pl={24} aria-live="polite">
        {item.conserto === 'preparar' && (preparar.isPending ? (
          <LeituraDoDevops estado="carregando" />
        ) : celular ? (
          <Text size="xs" c="dimmed" style={BARLOW}>Preparar só no computador.</Text>
        ) : (
          <Group gap="xs">
            <Button size="xs" variant="default" onClick={() => preparar.mutate()}>Preparar agora</Button>
          </Group>
        ))}
        {item.conserto === 'chamado' && (chamado.data ? (
          <Text size="xs" style={BARLOW}>Chamado <Dado>{chamado.data.protocolo}</Dado> aberto para o DevOps.</Text>
        ) : (
          <Group gap="xs">
            <Button size="xs" variant="default" loading={chamado.isPending} onClick={() => chamado.mutate()}>
              Abrir chamado
            </Button>
          </Group>
        ))}
        {item.estado === 'nao_conferido' && (
          <Anchor component="button" type="button" size="xs" onClick={onConferir}>conferir de novo</Anchor>
        )}
        {aviso && <Text size="xs" style={BARLOW} role="status">{aviso}</Text>}
      </Stack>
    </Stack>
  )
}

export function PassoProntidao({ tenantId, rascunhoId, prontidao }: PassoProps & {
  prontidao: UseQueryResult<ProntidaoDaConta>
}) {
  const celular = useMediaQuery('(max-width: 768px)') ?? false
  const conferir = () => { void prontidao.refetch() }

  if (prontidao.isLoading) return <LeituraDoDevops estado="carregando" />
  if (prontidao.error || !prontidao.data) {
    const negado = axios.isAxiosError(prontidao.error) && prontidao.error.response?.status === 403
    return (
      <LeituraDoDevops estado={negado ? 'sem_permissao' : 'erro'}
                       motivo={motivoDoErro(prontidao.error)} onTentarDeNovo={conferir} />
    )
  }
  const { itens, conferido_em } = prontidao.data
  return (
    <Stack gap="md">
      <Group gap="xs" wrap="wrap">
        <Text size="xs" c="dimmed" style={BARLOW}>
          Conta conferida pelo @DevOps em {new Date(conferido_em).toLocaleString('pt-BR')}.
        </Text>
        <Button size="xs" variant="subtle" onClick={conferir} loading={prontidao.isFetching}
                leftSection={<IconRefresh size={14} stroke={1.5} />}>
          Conferir de novo
        </Button>
      </Group>
      {itens.map((item) => (
        <ItemDaConta key={item.nome} item={item} tenantId={tenantId} rascunhoId={rascunhoId}
                     celular={celular} onConferir={conferir} />
      ))}
    </Stack>
  )
}
